/**
 * The `Campaign Delivery` tile data source.
 *
 * A `CustomTile` built to chart a campaign's delivery/assessment progress carries a query with
 * `dataSource === 'Campaign Delivery'` and a `campaignDeliveryId`. When the tile renders on a
 * dashboard (outside a builder preview, where data is fed inline), the tile fetch pipeline calls
 * `resolveCampaignDeliveryData` to pull that delivery's rows from toolbox_db and flatten them into a
 * chart-friendly row array. This mirrors the `File Import` source seam (a recognizer + an async
 * resolver returning a flat row array), but reads live campaign tables rather than a persisted
 * `ImportedDataset`.
 *
 * It is deliberately campaign-agnostic: the query names a *delivery id*, never a campaign key or any
 * campaign-specific field, so ANY campaign's deliveries chart through the exact same source with no
 * code change — campaigns DECLARE their deliveries, this engine CONSUMES them.
 */
import { fetchItems } from '../tileQueries';

export const CAMPAIGN_DELIVERY_SOURCE = 'Campaign Delivery';

/**
 * Which of a delivery's related tables backs the tile. Step statuses by default (completion charts).
 *
 * `install_base` is a special, campaign-resolved dataset: the numbers live in the SE's per-delivery
 * workshop state (the parsed Cisco Ready export's lifecycle counts), NOT in a queryable toolbox_db
 * table, so the generic resolver here returns nothing for it — the Frontier runner fills that figure
 * locally from its own WorkshopState. It is named in the union so an admin can bind a figure to it
 * and the type-checker accepts the query.
 */
export type CampaignDeliveryDataset = 'step_status' | 'target' | 'install_base';

export interface CampaignDeliveryQueryLike {
  dataSource?: string;
  campaignDeliveryId?: number;
  campaignDeliveryDataset?: CampaignDeliveryDataset;
}

/** True when a query should be served from a campaign delivery's live rows rather than fetched by path. */
export function isCampaignDeliveryQuery(q: CampaignDeliveryQueryLike | undefined | null): boolean {
  return !!q && q.dataSource === CAMPAIGN_DELIVERY_SOURCE && q.campaignDeliveryId != null;
}

/** One step-status row flattened for charting: status distribution and completion per step. */
function flattenStepStatus(row: any): Record<string, any> {
  return {
    step: row?.step_name ?? (row?.step != null ? `Step ${row.step}` : 'Unknown'),
    stepKind: row?.step_kind ?? '',
    status: row?.status ?? 'not_started',
    completed: row?.status === 'completed' ? 1 : 0,
    completedAt: row?.completed_at ?? null,
    targetId: row?.target ?? null,
  };
}

/** One delivery target flattened for charting: per-customer status. */
function flattenTarget(row: any): Record<string, any> {
  return {
    customer: row?.customer_name ?? row?.customer ?? 'Unknown',
    status: row?.status ?? 'not_started',
    opportunity: row?.opportunity ?? null,
    count: 1,
  };
}

/**
 * Resolve the rows for a `Campaign Delivery` query: the delivery's step statuses (default) or its
 * targets, flattened into a chart-ready array. Returns `[]` on any miss so a tile degrades to empty
 * rather than throwing inside the render pipeline. All fetches go through the shared authed
 * `fetchItems` (never a bare relative fetch, which drops auth in local dev).
 */
export async function resolveCampaignDeliveryData(q: CampaignDeliveryQueryLike): Promise<any[]> {
  if (!q?.campaignDeliveryId) return [];
  const dataset: CampaignDeliveryDataset = q.campaignDeliveryDataset ?? 'step_status';
  // Resolved by the owning campaign runner from its per-delivery workshop state, not from a queryable
  // table — nothing to fetch here (see the `install_base` note on CampaignDeliveryDataset).
  if (dataset === 'install_base') return [];
  try {
    if (dataset === 'target') {
      const rows = await fetchItems('db/campaign-delivery-target', {
        delivery: q.campaignDeliveryId,
      });
      return Array.isArray(rows) ? rows.map(flattenTarget) : [];
    }
    const rows = await fetchItems('db/campaign-delivery-step-status', {
      delivery: q.campaignDeliveryId,
    });
    return Array.isArray(rows) ? rows.map(flattenStepStatus) : [];
  } catch {
    return [];
  }
}
